import type { ExecutionStep, RunOutcome, RunPhase } from './dock/ExecutionLog';
import type { ProcessNode, ProcessEdge } from './graphTypes';
import type { NodeLiveState, ActiveRun } from './useProcessRunSession';
import { orderNodeIdsByExecution } from './executionOrder';

export interface ExecutionLogData {
  readonly phase: RunPhase;
  readonly order: ExecutionStep[];
  readonly total: number;
  readonly currentId: string | null;
  readonly startedAt: number | null;
  readonly finishedAt: number | null;
  readonly runOutcome: RunOutcome;
  /**
   * Per-process run number for the active run. Forwarded to the
   * sidebar ExecutionLog so it can render "Run #N" for non-test runs.
   */
  readonly runNumber: number | undefined;
  /**
   * Whether the active run is a test run (drives the sidebar title
   * "Test run" vs "Run #N").
   */
  readonly isTest: boolean | undefined;
}

/**
 * Derive the sidebar ExecutionLog props from the live SSE session state.
 *
 * Orders the graph nodes by the order the run reached them, then looks up
 * each node's live status to find which one is currently running. Returns
 * `null` when there is no active run — the caller should hide the
 * ExecutionLog in that case.
 */
export function buildLiveExecutionSteps(
  active: ActiveRun | null,
  perNode: ReadonlyMap<string, NodeLiveState>,
  nodes: readonly ProcessNode[],
  edges: readonly ProcessEdge[],
): ExecutionLogData | null {
  if (!active) return null;

  // Only include computable nodes (skip notes).
  const computable = nodes.filter((n) => n.type !== 'note');
  const computableIds = orderNodeIdsByExecution(
    computable,
    edges,
    (id) => perNode.get(id)?.startedSeq ?? null,
  );

  const order: ExecutionStep[] = computableIds.map((id) => {
    const node = nodes.find((n) => n.id === id);
    const label =
      (node?.data as { label?: string } | undefined)?.label ?? id;
    const live = perNode.get(id);
    return { id, title: label, nodeStatus: live?.status };
  });

  let currentId: string | null = null;
  for (const id of computableIds) {
    const live = perNode.get(id);
    if (
      live?.status === 'running' ||
      live?.status === 'provisioning' ||
      live?.status === 'waiting_for_runner'
    ) {
      currentId = id;
      break;
    }
  }

  const isTerminal =
    active.runStatus === 'completed' ||
    active.runStatus === 'failed' ||
    active.runStatus === 'cancelled';

  const startMs = active.startedAt
    ? new Date(active.startedAt).getTime()
    : null;

  let runOutcome: RunOutcome = 'completed';
  if (active.runStatus === 'failed') runOutcome = 'failed';
  else if (active.runStatus === 'cancelled') runOutcome = 'cancelled';

  return {
    phase: isTerminal ? 'done' : 'running',
    order,
    total: order.length,
    currentId,
    startedAt: startMs,
    finishedAt: null,
    runOutcome,
    runNumber: active.runNumber,
    isTest: active.isTest,
  };
}
